import { StandardEvents } from '@shopify/events';

const idleActivityEvents = ['pointerdown', 'keydown', 'wheel', 'touchstart', 'input', 'focusin'];

class GooglyEyes extends HTMLElement {
  #animationFrame;
  #blinkTimer;
  #blinkEndTimer;
  #intersectionObserver;
  #isVisible = false;
  #lastPointerMovement = performance.now();
  #pointer;
  #motionQuery;
  #pointerQuery;
  #winkTimer;
  #connection = 0;
  #idleTimer;
  #sleepTimer;

  #resetPose = () => {
    if (this.#animationFrame) cancelAnimationFrame(this.#animationFrame);
    this.#animationFrame = undefined;
    this.#pointer = undefined;
    this.removeAttribute('data-tracking');
    for (const pupil of this.querySelectorAll('[data-googly-eye-pupil]')) {
      pupil.style.removeProperty('--googly-eye-x');
      pupil.style.removeProperty('--googly-eye-y');
    }
  };

  #handlePointerOut = (event) => {
    if (!event.relatedTarget) this.#resetPose();
  };

  #handlePreferenceChange = () => {
    this.#stopWink();
    this.#stopSleep();
    this.#resetPose();
    this.#clearBlinkTimers();
    this.removeAttribute('data-blinking');
    if (!this.#motionQuery.matches) this.#queueBlink();
    this.#queueSleep();
  };

  #randomBlinkDelay() {
    return 4000 + Math.random() * 4000;
  }

  #clearBlinkTimers() {
    if (this.#blinkTimer) clearTimeout(this.#blinkTimer);
    if (this.#blinkEndTimer) clearTimeout(this.#blinkEndTimer);
    this.#blinkTimer = undefined;
    this.#blinkEndTimer = undefined;
  }

  #queueBlink = (delay = this.#randomBlinkDelay()) => {
    if (!this.hasAttribute('data-blink') || this.#motionQuery?.matches || this.hasAttribute('data-winking') || this.hasAttribute('data-sleeping')) return;

    if (this.#blinkTimer) clearTimeout(this.#blinkTimer);
    this.#blinkTimer = setTimeout(this.#tryBlink, delay);
  };

  #tryBlink = () => {
    this.#blinkTimer = undefined;
    if (this.hasAttribute('data-winking') || this.hasAttribute('data-sleeping')) return;

    if (!this.#isVisible || document.visibilityState !== 'visible') {
      this.#queueBlink();
      return;
    }

    const idleFor = performance.now() - this.#lastPointerMovement;
    if (idleFor < 2500) {
      this.#queueBlink(2500 - idleFor + 500 + Math.random() * 1000);
      return;
    }

    this.setAttribute('data-blinking', '');
    this.#blinkEndTimer = setTimeout(() => {
      this.removeAttribute('data-blinking');
      this.#blinkEndTimer = undefined;
      this.#queueBlink();
    }, 220);
  };

  #handleVisibilityChange = () => {
    if (document.visibilityState === 'visible') {
      this.#lastPointerMovement = performance.now();
      this.#queueBlink();
      this.#queueSleep();
    } else {
      this.#stopSleep();
      this.#stopWink();
      this.#resetPose();
      this.#clearBlinkTimers();
      this.removeAttribute('data-blinking');
    }
  };

  #handlePointerMove = (event) => {
    this.#handleActivity();
    if (this.hasAttribute('data-winking') || !this.#isVisible || document.visibilityState !== 'visible' || this.#motionQuery.matches || !this.#pointerQuery.matches || event.pointerType === 'touch') return;
    this.#lastPointerMovement = performance.now();
    this.#pointer = { x: event.clientX, y: event.clientY };
    if (this.#animationFrame) return;

    this.#animationFrame = requestAnimationFrame(() => {
      this.#animationFrame = undefined;

      if (!this.#pointer) return;
      const bounds = this.getBoundingClientRect();
      const deltaX = this.#pointer.x - (bounds.left + bounds.width / 2);
      const deltaY = this.#pointer.y - (bounds.top + bounds.height / 2);
      if (Math.hypot(deltaX, deltaY) < 0.5) return;

      // Keep the supplied perimeter orbit. Only the right pupil needs less
      // leftward travel where the two eye shapes overlap.
      // Values are SVG user units, so this scales with both logo placements.
      const angle = Math.atan2(deltaY, deltaX);
      const horizontalOffset = Math.cos(angle) * 12.4;
      const verticalOffset = Math.sin(angle) * 12.4;
      for (const [index, pupil] of this.querySelectorAll('[data-googly-eye-pupil]').entries()) {
        const x = index === 1 ? Math.max(horizontalOffset, -8.5) : horizontalOffset;
        pupil.style.setProperty('--googly-eye-x', `${x}px`);
        pupil.style.setProperty('--googly-eye-y', `${verticalOffset}px`);
      }
      this.setAttribute('data-tracking', '');
    });
  };

  #canAnimate() {
    return (
      this.isConnected && this.#isVisible &&
      document.visibilityState === 'visible' && !this.#motionQuery?.matches &&
      this.getClientRects().length > 0 && getComputedStyle(this).visibility === 'visible' &&
      this.checkVisibility?.({ checkOpacity: true, checkVisibilityCSS: true }) !== false
    );
  }

  #handleCartUpdate = async (event) => {
    if (event.action !== 'add' || !event.promise?.then || !(this.hasAttribute('data-wink-after-add') && this.#canAnimate()) || this.hasAttribute('data-winking')) return;
    const connection = this.#connection;
    try {
      const result = await event.promise;
      if (
        connection !== this.#connection || !result?.cart || result.detail?.didError || result.userErrors?.length ||
        !(this.hasAttribute('data-wink-after-add') && this.#canAnimate()) || this.hasAttribute('data-winking')
      ) return;
      this.#stopSleep();
      this.#resetPose();
      this.#clearBlinkTimers();
      this.removeAttribute('data-blinking');
      this.setAttribute('data-winking', '');
      // animationend owns the 800ms finish; fallback restores rest if CSS cannot run.
      this.#winkTimer = setTimeout(this.#finishWink, 900);
    } catch {
      // Failed requests keep the original artwork and existing cart error feedback.
    }
  };

  #stopWink = () => {
    if (this.#winkTimer) clearTimeout(this.#winkTimer);
    this.#winkTimer = undefined;
    this.removeAttribute('data-winking');
    this.#resetPose();
  };

  #finishWink = () => {
    this.#stopWink();
    this.#lastPointerMovement = performance.now();
    if (this.#isVisible && document.visibilityState === 'visible') this.#queueBlink();
    this.#queueSleep();
  };

  #handleAnimationEnd = (event) => {
    if (event.animationName === 'googly-eyes-bag-wink' && this.hasAttribute('data-winking')) this.#finishWink();
    if (event.animationName === 'googly-eyes-sleep-wake' && this.hasAttribute('data-sleeping')) this.#finishSleep();
  };

  #handleBlur = () => {
    this.#stopSleep();
    this.#stopWink();
    this.#queueBlink();
  };

  #seconds(attribute, fallback, min, max) {
    const value = Number(this.getAttribute(attribute));
    return Number.isFinite(value) && value >= min && value <= max ? value : fallback;
  }

  #queueSleep = () => {
    if (this.#idleTimer) clearTimeout(this.#idleTimer);
    this.#idleTimer = undefined;
    if (!this.hasAttribute('data-idle-sleep') || !this.#canAnimate() ||
      this.hasAttribute('data-winking') || this.hasAttribute('data-sleeping')) return;
    const delay = this.#seconds('data-idle-delay', 90, 5, 300);
    this.#idleTimer = setTimeout(this.#startSleep, delay * 1000);
  };

  #startSleep = () => {
    this.#idleTimer = undefined;
    if (!this.hasAttribute('data-idle-sleep') || !this.#canAnimate() || this.hasAttribute('data-winking')) return;
    this.#resetPose();
    this.#clearBlinkTimers();
    this.removeAttribute('data-blinking');
    const duration = this.#seconds('data-sleep-duration', 10, 3, 30) * 1000;
    this.style.setProperty('--googly-sleep-duration', `${duration}ms`);
    this.setAttribute('data-sleeping', '');
    // CSS animates close/hold/wake; this also restores rest if animationend is lost.
    this.#sleepTimer = setTimeout(this.#finishSleep, duration + 100);
  };

  #stopSleep = () => {
    if (this.#idleTimer) clearTimeout(this.#idleTimer);
    if (this.#sleepTimer) clearTimeout(this.#sleepTimer);
    this.#idleTimer = undefined;
    this.#sleepTimer = undefined;
    if (this.hasAttribute('data-sleeping')) this.#resetPose();
    this.removeAttribute('data-sleeping');
    this.style.removeProperty('--googly-sleep-duration');
  };

  #finishSleep = () => {
    this.#stopSleep();
    this.#lastPointerMovement = performance.now();
    if (this.#canAnimate()) this.#queueBlink();
    this.#queueSleep();
  };

  #handleActivity = () => {
    if (!this.hasAttribute('data-idle-sleep')) return;
    const sleeping = this.hasAttribute('data-sleeping');
    this.#stopSleep();
    this.#lastPointerMovement = performance.now();
    if (sleeping && this.#canAnimate()) this.#queueBlink();
    this.#queueSleep();
  };

  connectedCallback() {
    this.#connection++;
    this.#stopSleep();
    this.#stopWink();
    this.#isVisible = false;
    this.#resetPose();
    this.#motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.#pointerQuery = window.matchMedia('(min-width: 750px) and (hover: hover) and (pointer: fine)');
    this.#motionQuery.addEventListener('change', this.#handlePreferenceChange);
    this.#pointerQuery.addEventListener('change', this.#handlePreferenceChange);

    window.addEventListener('pointermove', this.#handlePointerMove, { passive: true });
    window.addEventListener('blur', this.#handleBlur);
    window.addEventListener('focus', this.#queueSleep);
    if (this.hasAttribute('data-idle-sleep')) {
      // Nested carousels may scroll automatically; their user input is covered above.
      window.addEventListener('scroll', this.#handleActivity, { passive: true });
      for (const type of idleActivityEvents) document.addEventListener(type, this.#handleActivity, { passive: true, capture: true });
    }
    document.addEventListener('pointerout', this.#handlePointerOut);
    document.addEventListener('visibilitychange', this.#handleVisibilityChange);
    if (this.hasAttribute('data-wink-after-add')) document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate);
    this.addEventListener('animationend', this.#handleAnimationEnd);

    this.#intersectionObserver = new IntersectionObserver(([entry]) => {
      this.#isVisible = entry.isIntersecting;

      if (this.#isVisible) {
        this.#queueBlink();
        this.#queueSleep();
      } else {
        this.#stopSleep();
        this.#stopWink();
        this.#resetPose();
        this.#clearBlinkTimers();
        this.removeAttribute('data-blinking');
      }
    });
    this.#intersectionObserver.observe(this);
  }

  disconnectedCallback() {
    this.#connection++;
    this.#stopSleep();
    this.#stopWink();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate);
    this.removeEventListener('animationend', this.#handleAnimationEnd);
    this.#isVisible = false;
    window.removeEventListener('pointermove', this.#handlePointerMove);
    window.removeEventListener('blur', this.#handleBlur);
    window.removeEventListener('focus', this.#queueSleep);
    window.removeEventListener('scroll', this.#handleActivity);
    for (const type of idleActivityEvents) document.removeEventListener(type, this.#handleActivity, { capture: true });
    document.removeEventListener('pointerout', this.#handlePointerOut);
    this.#motionQuery?.removeEventListener('change', this.#handlePreferenceChange);
    this.#pointerQuery?.removeEventListener('change', this.#handlePreferenceChange);
    document.removeEventListener('visibilitychange', this.#handleVisibilityChange);
    this.#intersectionObserver?.disconnect();
    this.#clearBlinkTimers();
    this.removeAttribute('data-blinking');
    this.#resetPose();
  }
}

if (!customElements.get('googly-eyes')) {
  customElements.define('googly-eyes', GooglyEyes);
}
